import { useCallback } from 'react';

import { useGuardedNavigate } from './NavigationGuardContext';

/** The three views of the floor plan editor demo page. */
export type FloorPlanEditorDemoView =
  | { view: 'room' }
  | { view: 'floorPlan' }
  | { view: 'propertyRoom'; roomId: string };

export const FLOOR_PLAN_EDITOR_DEMO_PATH = '/floor-plan-editor-demo';

const FLOOR_PLAN_SEGMENT = '/floor-plan';
const PROPERTY_ROOM_SEGMENT = `${FLOOR_PLAN_SEGMENT}/room/`;

/** The URL that shows `view` on the floor plan editor demo page. */
export const getFloorPlanEditorDemoUrl = (view: FloorPlanEditorDemoView = { view: 'room' }): string => {
  switch (view.view) {
    case 'room':
      return FLOOR_PLAN_EDITOR_DEMO_PATH;
    case 'floorPlan':
      return `${FLOOR_PLAN_EDITOR_DEMO_PATH}${FLOOR_PLAN_SEGMENT}`;
    case 'propertyRoom':
      return `${FLOOR_PLAN_EDITOR_DEMO_PATH}${PROPERTY_ROOM_SEGMENT}${encodeURIComponent(view.roomId)}`;
  }
};

/** The view a floor plan editor demo path shows: the inverse of `getFloorPlanEditorDemoUrl`, with any unknown sub-path reading as the room editor. */
export const parseFloorPlanEditorDemoPath = (pathname: string): FloorPlanEditorDemoView => {
  const subPath = pathname.startsWith(FLOOR_PLAN_EDITOR_DEMO_PATH) ? pathname.slice(FLOOR_PLAN_EDITOR_DEMO_PATH.length).replace(/\/+$/, '') : '';
  if (subPath === FLOOR_PLAN_SEGMENT) return { view: 'floorPlan' };
  if (subPath.startsWith(PROPERTY_ROOM_SEGMENT) && subPath.length > PROPERTY_ROOM_SEGMENT.length) {
    return { view: 'propertyRoom', roomId: decodeURIComponent(subPath.slice(PROPERTY_ROOM_SEGMENT.length)) };
  }
  return { view: 'room' };
};

/** Navigates to a view of the floor plan editor demo, asking first when an editor has unsaved changes. */
export const useNavigateToFloorPlanEditorDemo = () => {
  const navigate = useGuardedNavigate();
  return useCallback((view: FloorPlanEditorDemoView = { view: 'room' }) => navigate(getFloorPlanEditorDemoUrl(view)), [navigate]);
};
